/**
 * Meeting Sidebar Component
 * 좌측 사이드바 (데스크톱 전용)
 */

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { meetingCategories } from "@shared/schema";
import {
  Calendar,
  FileText,
  Home,
  Megaphone,
  Smile,
  Trophy,
} from "lucide-react";

interface MeetingSidebarProps {
  meeting: {
    id: number;
    title: string;
    description: string;
    category: string;
    ageRange?: string | null;
    suburb: string | null;
    members: { total: number };
    stats: { totalPosts: number };
  };
  selectedPostCategory: string;
  onPostCategoryChange: (category: string) => void;
  onNavigateToSection?: (sectionId: string) => void;
  onJoinMeeting?: () => void;
  isJoined?: boolean;
}

export const boardCategories: Array<{
  id: string;
  label: string;
  icon: any;
  emoji?: string;
}> = [
  { id: "all", label: "전체", icon: FileText },
  { id: "free", label: "자유 게시판", icon: FileText },
  { id: "notice", label: "공지사항", icon: Megaphone, emoji: "✨" },
  { id: "review", label: "모임 후기", icon: Smile, emoji: "😀" },
  { id: "schedule_review", label: "일정 후기", icon: FileText },
  { id: "join_greeting", label: "가입인사", icon: FileText },
];

export function MeetingSidebar({
  meeting,
  selectedPostCategory,
  onPostCategoryChange,
  onNavigateToSection,
  onJoinMeeting,
  isJoined = false,
}: MeetingSidebarProps) {
  const categoryLabel =
    meetingCategories.find((c) => c.id === meeting.category)?.label ||
    meeting.category;

  return (
    <aside className="w-full lg:w-72 flex-shrink-0">
      <div className="sticky top-20 space-y-4">
        {/* 모임 정보 카드 */}
        <Card>
          <CardContent className="p-4 space-y-4">
            <div>
              <h2 className="text-lg font-semibold mb-1">{meeting.title}</h2>
              <p className="text-sm text-muted-foreground">
                멤버 {meeting.members.total} - 게시글 {meeting.stats.totalPosts}
              </p>
            </div>

            <div>
              <p className="text-sm text-muted-foreground line-clamp-3">
                {meeting.description}
              </p>
              <Button
                variant="ghost"
                className="p-0 h-auto text-xs text-muted-foreground hover:text-foreground"
              >
                더보기
              </Button>
            </div>

            <div className="flex flex-wrap gap-2">
              {meeting.suburb && (
                <Badge variant="secondary" className="text-xs">
                  {meeting.suburb}
                </Badge>
              )}
              {meeting.category && (
                <Badge variant="secondary" className="text-xs">
                  {categoryLabel}
                </Badge>
              )}
              {meeting.ageRange ? (
                <Badge variant="secondary" className="text-xs">
                  나이대 {meeting.ageRange}
                </Badge>
              ) : (
                <Badge variant="secondary" className="text-xs">
                  누구나
                </Badge>
              )}
            </div>

            {isJoined ? (
              <div className="w-full rounded-md border border-dashed border-muted-foreground/40 text-center py-2 text-sm text-muted-foreground">
                이미 참여 중인 모임입니다
              </div>
            ) : (
              <Button
                className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                onClick={onJoinMeeting}
              >
                참여하기
              </Button>
            )}
          </CardContent>
        </Card>

        {/* 네비게이션 메뉴 */}
        <Card>
          <CardContent className="p-2">
            <nav className="space-y-1">
              <Button
                variant="ghost"
                className="w-full justify-start"
                size="sm"
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                <Home className="w-4 h-4 mr-2" />홈
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start"
                size="sm"
                onClick={() => {
                  onNavigateToSection?.("posts");
                  onPostCategoryChange("notice");
                }}
              >
                <Megaphone className="w-4 h-4 mr-2" />
                공지사항
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start"
                size="sm"
                onClick={() => {
                  onNavigateToSection?.("events");
                }}
              >
                <Calendar className="w-4 h-4 mr-2" />
                모임 일정
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start"
                size="sm"
                disabled
                title="준비 중"
              >
                <Trophy className="w-4 h-4 mr-2" />
                챌린지
              </Button>
            </nav>
          </CardContent>
        </Card>

        {/* 게시판 카테고리 */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">게시판</CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            <nav className="space-y-1">
              {boardCategories.map((cat) => {
                const Icon = cat.icon;
                return (
                  <Button
                    key={cat.id}
                    variant="ghost"
                    className="w-full justify-start"
                    size="sm"
                    onClick={() => onPostCategoryChange(cat.id)}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {cat.emoji && <span className="mr-1">{cat.emoji}</span>}
                    {cat.label}
                    {cat.emoji && <span className="ml-1">{cat.emoji}</span>}
                  </Button>
                );
              })}
            </nav>
          </CardContent>
        </Card>
      </div>
    </aside>
  );
}
